import Promise from 'bluebird';
import _ from 'underscore';
import { useCallback, useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService, WIZARD_EVENTS, getIsLastStep, getCurrentStepId } from '@bingads-webui-react/wizard';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { readDayTimeCriterions, readLocationCriterions, readAssetGroup } from '@bingads-webui-campaign-react/perf-max-data';
import { loadBundleCountries, loadCampaign } from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useAudienceData } from '@bingads-webui-campaign-react/pmax-lite-data';
import { formatAudienceListToFlatTree } from '@bingads-webui-campaign-react/pmax-targeting';

import { saveCampaign } from '../model/campaign-bulk';
import { modelToViewModelForCampaign } from '../model/campaign';
import { WizardScenario } from './constants';
import { STEP_PATHS } from '../step-constants';
import { saveAssetGroupAndLinkedData, modelToViewModelForAssetGroup, modelToViewModelForAudienceGroup, getErrorsPlaceholders } from '../model/asset-group';

export const useCampaignRepository = () => {
  const i18n = useContext(I18nContext);
  const { state, send } = useWizardService();
  const currentStepId = getCurrentStepId(state);
  const { scenario } = state.context.wizardData;
  const isLastStep = getIsLastStep(state);
  const serverContext = useContext(ServerContext);
  const { scope } = useContext(ScopeContext) || {};
  const scenarioContext = useContext(ScenarioContext);
  const [, getAudienceData] = useAudienceData({ postponeFetch: true, pathOverride: 'Campaign/V2' });

  const activity = useContext(ActivityContext);

  const audienceGroupEnabled = _.get(serverContext, ['DynamicPermissions', 'WindowsStoreAudienceGroupAssociation']);
  const locationTargetingEnabled = _.get(serverContext, ['DynamicPermissions', 'WindowsStoreLocationTargeting']);

  const shouldSaveCampaign = currentStepId === STEP_PATHS.CAMPAIGN_SETTING ||
  _.contains([WizardScenario.CampaignCreation, WizardScenario.CampaignEdit], scenario);
  const shouldLoadAssetGroup = currentStepId === STEP_PATHS.CAMPAIGN_TARGETING &&
  _.contains([WizardScenario.CampaignCreation, WizardScenario.AssetGroupEdit, WizardScenario.AssetGroupCreation], scenario);

  const {
    cid, aid, campaignId: campaignIdFromScope, assetGroupId: assetGroupIdFromScope,
  } = scope.values;

  const loadAudienceData = useCallback(() => getAudienceData().then(audienceList => _.filter(audienceList, audience => audience && audience.Type === 'InMarket')).catch(error => Promise.reject(error)), [getAudienceData]);

  const load = () => {
    const savedCampaign = state.context.wizardData.campaign;
    const savedAssetGroup = state.context.wizardData.assetGroup;
    const campaignId = campaignIdFromScope || (savedCampaign ? savedCampaign.Id : null);
    const assetGroupId = assetGroupIdFromScope || (savedAssetGroup ? savedAssetGroup.Id : null);

    const campaignLoadPromises = campaignId ? {
      campaign: loadCampaign(cid, aid, campaignId),
      dayTimeCriterions: readDayTimeCriterions(cid, aid, campaignId),
      locationCriterions: readLocationCriterions(cid, aid, campaignId),
      bundleCountriesAndLocationCriterions: locationTargetingEnabled && loadBundleCountries({
        cid,
        aid,
        serverContext,
      }),
    } : {};

    let assetGroupLoadPromises = shouldLoadAssetGroup && audienceGroupEnabled ? {
      audienceList: loadAudienceData(),
    } : {};

    if (assetGroupId && shouldLoadAssetGroup) {
      assetGroupLoadPromises = _.extend(assetGroupLoadPromises, {
        assetGroup: readAssetGroup(cid, aid, campaignId, assetGroupId),
      });
    }

    const loadPromises = {
      ...campaignLoadPromises,
      ...assetGroupLoadPromises,
    };

    return Promise.props(loadPromises).then(({
      campaign,
      dayTimeCriterions,
      locationCriterions,
      bundleCountriesAndLocationCriterions,
      assetGroup,
      audienceList,
    }) => {
      let response = {
        ...getErrorsPlaceholders(),
      };

      if (campaign) {
        const campaignData = modelToViewModelForCampaign({
          campaignModel: campaign,
          locationCriterionsModel: locationCriterions ? locationCriterions.value : [],
          dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
          ...bundleCountriesAndLocationCriterions,
        });
        response = _.extend(response, {
          campaign: campaignData.data,
          ...bundleCountriesAndLocationCriterions,
        });
      }

      let audienceListMap;
      if (audienceGroupEnabled && audienceList) {
        const [audienceListInFlatTree, valueToAudience, valueToFlatItem] = formatAudienceListToFlatTree(audienceList);
        audienceListMap = audienceListInFlatTree;
        response = _.extend(response, {
          audiencesSignal: {
            audienceListInFlatTree,
            valueToAudience,
            valueToFlatItem,
          },
        });
      }

      const assetGroupPromise = assetGroup ? modelToViewModelForAssetGroup(assetGroup, i18n, cid, aid) : Promise.resolve({ data: null });

      return assetGroupPromise.then((assetGroupData) => {
        response = _.extend(response, {
          assetGroup: assetGroupData.data,
        });
        if (audienceGroupEnabled && assetGroup) {
          response = _.extend(response, {
            audiencesSignal: {
              ...response.audiencesSignal,
              excludedAudiences: [],
              ...modelToViewModelForAudienceGroup(assetGroupData.data, audienceListMap),
            },
          });
        }
        return response;
      });
    });
  };

  const allSettledPromiseWrapper = (promises) => {
    const wrappedPromises = promises.map(p => Promise.resolve(p)
      .then(
        val => ({ status: 'fulfilled', value: val }),
        err => ({ status: 'rejected', reason: err })
      ));
    return Promise.all(wrappedPromises);
  };

  const save = ({ data }) => {
    const {
      campaign,
      assetGroup,
      audiencesSignal,
      bundleCountries,
      bundleLocationCriterions,
    } = data;

    if (isLastStep) {
      campaign.Status = 'Active';
    } else {
      campaign.Status = 'Paused';
    }

    const savePromises = [];

    if (shouldLoadAssetGroup) {
      const saveAssetGroupPromise = saveAssetGroupAndLinkedData({
        assetGroup: { data: assetGroup },
        originalAssetGroup: { data: data.assetGroup }, // existing model data
        campaign,
        audiencesSignal,
      }, {
        scenario: scenarioContext.name,
        i18n,
        activityLogger: scenarioContext.createActivity('saveDefaultAssetGroup'),
        serverContext,
        cid,
        aid,
      });
      savePromises.push(saveAssetGroupPromise);
    }

    if (shouldSaveCampaign) {
      const saveCampaignPromise = saveCampaign({
        campaign: { data: campaign },
        assetGroup: { data: assetGroup },
        bundleCountries,
        bundleLocationCriterions,
      }, {
        scenario: scenarioContext.name,
        activityLogger: scenarioContext.createActivity('saveDefaultCampaign'),
        i18n,
      });
      savePromises.push(saveCampaignPromise);
    }

    return new Promise((resolve, reject) => {
      allSettledPromiseWrapper(savePromises).then((results) => {
        const allSuccess = _.every(results, result => result.status === 'fulfilled');

        if (allSuccess) {
          if (activity) {
            activity.signal(`onBudgetAndBidSavedSuccessfully - Status: ${campaign.Status}, Id: ${campaign.Id}`);
          }
          const campaignResult = results.find(r => 'campaign' in r.value);
          if (campaignResult) {
            send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaign: campaignResult.value.campaign.data });
          }

          const assetGroupResult = results.find(r => 'assetGroup' in r.value);
          if (assetGroupResult) {
            send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { assetGroup: assetGroupResult.value.assetGroup.data });
          }

          return resolve({
            campaign: campaignResult && campaignResult.value.campaign.data,
            assetGroup: assetGroupResult && assetGroupResult.value.assetGroup.data,
            ...getErrorsPlaceholders(),
          });
        }

        const errors = _.map(results, (result) => {
          if (activity) {
            activity.signal(`OnBudgetAndBidSavedError - error: ${JSON.stringify(result)}`);
          }

          if (result.status === 'fulfilled') {
            return null;
          }

          const { serverErrors = {} } = result.reason;

          return serverErrors;
        });

        // eslint-disable-next-line prefer-promise-reject-errors
        return reject({
          error: _.flatten(errors.filter(val => val !== null)),
        });
      });
    });
  };

  return {
    load,
    save,
  };
};
